import { useEffect, useRef, useState } from "react";

import {
  IMAGE_ACCEPT,
  IMAGE_ACCEPT_HINT,
  isAcceptedImageFile,
  UNSUPPORTED_IMAGE_MESSAGE,
} from "../imageFileContract";
import SharedImageEditorModal from "../imageEditorModal/SharedImageEditorModal";
import {
  QUEUE_PHASE,
  canRetryPrepare,
  canRetryUpload,
  queuePhaseLabel,
} from "../propertyMediaQueue/propertyMediaQueue";
import { moveMediaByOffset } from "../mediaReorder/mediaReorder";
import { usePointerReorder } from "../mediaReorder/usePointerReorder";
import PropertyMediaDeleteDialog from "./PropertyMediaDeleteDialog";
import { fileFromMediaUrl } from "./fileFromMediaUrl";
import {
  PROPERTY_MEDIA_MANAGER,
  sortPropertyImages,
} from "./propertyMediaOrder";

const FALLBACK_IMAGE = "/images/no-photo.png";

function Dropzone({ inputId, title, disabled, onFiles }) {
  const [dragOver, setDragOver] = useState(false);
  const [localError, setLocalError] = useState(null);

  const handleFiles = (fileList) => {
    if (disabled) return;
    const files = Array.from(fileList || []);
    const accepted = [];
    let rejected = false;
    files.forEach((file) => {
      if (isAcceptedImageFile(file)) accepted.push(file);
      else rejected = true;
    });
    if (rejected && accepted.length === 0) {
      setLocalError(UNSUPPORTED_IMAGE_MESSAGE);
      return;
    }
    setLocalError(rejected ? UNSUPPORTED_IMAGE_MESSAGE : null);
    if (accepted.length) onFiles(accepted);
  };

  return (
    <>
      <label
        className={dragOver ? "ap-dropzone is-dragover" : "ap-dropzone"}
        htmlFor={inputId}
        onDragOver={(event) => {
          if (disabled) return;
          event.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(event) => {
          if (disabled) return;
          event.preventDefault();
          setDragOver(false);
          handleFiles(event.dataTransfer.files);
        }}
      >
        <input
          id={inputId}
          type="file"
          accept={IMAGE_ACCEPT}
          multiple
          disabled={disabled}
          onChange={(event) => {
            handleFiles(event.target.files);
            event.target.value = "";
          }}
        />
        <span className="ap-dropzone__icon">
          <i className="fa-solid fa-cloud-arrow-up" aria-hidden="true" />
        </span>
        <span className="ap-dropzone__title">{title}</span>
        <span className="ap-dropzone__sub">
          {disabled ? "Зачекайте…" : IMAGE_ACCEPT_HINT}
        </span>
      </label>
      {localError ? (
        <p className="form-hint text-danger" role="alert">
          {localError}
        </p>
      ) : null}
    </>
  );
}

function KindBadge({ kind }) {
  return (
    <span className="property-media-card__kind">
      {kind === "plan" ? "План" : "Фото"}
    </span>
  );
}

function MainBadge() {
  return (
    <span className="property-media-card__main">
      <i className="fa-solid fa-star" aria-hidden="true" />
      ГОЛОВНЕ
    </span>
  );
}

function LocalStatus({ item }) {
  const label = queuePhaseLabel(item.phase);
  if (item.phase === QUEUE_PHASE.prepare_failed && item.prepareError) {
    return (
      <span className="property-media-card__status" role="alert">
        {item.prepareError}
      </span>
    );
  }
  if (item.phase === QUEUE_PHASE.upload_failed && item.uploadError) {
    return (
      <span className="property-media-card__status" role="alert">
        {item.uploadError}
      </span>
    );
  }
  if (
    item.phase === QUEUE_PHASE.checking
    || item.phase === QUEUE_PHASE.server_preview
    || item.phase === QUEUE_PHASE.uploading
    || item.phase === QUEUE_PHASE.queued
  ) {
    return (
      <span className="property-media-card__status" role="status">
        {label}
      </span>
    );
  }
  return null;
}

/**
 * Shared create/edit Property media board.
 * Does not import presentation authorization or PropertyShare state.
 */
function PropertyMediaBoard({
  kind,
  title,
  dropTitle,
  inputId,
  serverItems,
  localItems,
  busy = false,
  canReorder = false,
  allowMakeMain = false,
  showPendingMain = false,
  pendingMainLocalId = null,
  reorderError = null,
  onAdd,
  onReorder,
  onMakeMain,
  onMakePendingMain,
  onDeleteServer,
  onDeleteLocal,
  onRetryPrepare,
  onRetryUpload,
  onEditItem,
  editingItem,
  onApplyEdit,
  onReplaceServer,
  onCancelEdit,
}) {
  const listRef = useRef(null);
  const idsRef = useRef([]);
  const serverItemsRef = useRef(serverItems);
  const restoreFocusRef = useRef(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const orderedServer = sortPropertyImages(serverItems);
  const ids = orderedServer.map((img) => img.id);
  idsRef.current = ids;
  serverItemsRef.current = orderedServer;

  const applyOrder = (nextIds) => {
    const next = Array.isArray(nextIds) ? [...nextIds] : [];
    const current = idsRef.current;
    if (
      next.length === current.length
      && next.every((id, index) => id === current[index])
    ) {
      return;
    }
    idsRef.current = next;
    onReorder?.(next);
  };

  const { draggingId, onHandlePointerDown, onHandlePointerUp, endDrag } = usePointerReorder({
    rootRef: listRef,
    getIds: () => idsRef.current || [],
    onReorder: (next) => applyOrder(next),
    enabled: canReorder && !busy,
  });

  const onHandleKeyDown = (event, imageId) => {
    if (!canReorder || busy) return;
    if (event.key === "ArrowLeft" || event.key === "ArrowUp") {
      event.preventDefault();
      applyOrder(moveMediaByOffset(idsRef.current || [], imageId, -1));
      return;
    }
    if (event.key === "ArrowRight" || event.key === "ArrowDown") {
      event.preventDefault();
      applyOrder(moveMediaByOffset(idsRef.current || [], imageId, 1));
    }
  };

  const editorOpen =
    editingItem
    && editingItem.kind === kind
    && (
      (editingItem.localId != null
        && (localItems || []).some((item) => item.localId === editingItem.localId))
      || (editingItem.imageId != null
        && orderedServer.some((img) => img.id === editingItem.imageId))
    );
  const editorSource = editorOpen && editingItem.localId != null
    ? (localItems || []).find((item) => item.localId === editingItem.localId)
    : null;
  const [serverSourceFile, setServerSourceFile] = useState(null);
  const [serverSourceError, setServerSourceError] = useState(null);

  useEffect(() => {
    if (!editorOpen || editingItem?.imageId == null || !editingItem.sourceUrl) {
      setServerSourceFile(null);
      setServerSourceError(null);
      return undefined;
    }
    let cancelled = false;
    setServerSourceFile(null);
    setServerSourceError(null);
    fileFromMediaUrl(
      editingItem.sourceUrl,
      `property-image-${editingItem.imageId}.jpg`,
    )
      .then((file) => {
        if (!cancelled) setServerSourceFile(file);
      })
      .catch((err) => {
        if (!cancelled) {
          setServerSourceError(
            (err && err.message) || "Не вдалося відкрити зображення для редагування.",
          );
        }
      });
    return () => {
      cancelled = true;
    };
  }, [editorOpen, editingItem]);

  const noun = kind === "plan" ? "план" : "фото";
  const confirmTitle = kind === "plan" ? "Видалити план?" : "Видалити фото?";

  return (
    <div
      className="ap-uploader property-media-board"
      data-property-media-manager={PROPERTY_MEDIA_MANAGER}
      data-testid={`property-media-board-${kind}`}
    >
      <h3>
        <i
          className={kind === "plan" ? "fa-solid fa-vector-square" : "fa-solid fa-images"}
          aria-hidden="true"
        />
        {title}
      </h3>

      {reorderError ? (
        <p className="form-hint text-danger" role="alert">
          {reorderError}
        </p>
      ) : null}

      <ul
        ref={listRef}
        className="property-media-board__list"
        aria-label={title}
      >
        {orderedServer.map((img, idx) => {
          const isMain = Boolean(img.is_main);
          return (
            <li
              key={`server-${img.id}`}
              className={[
                "property-media-card",
                isMain ? "property-media-card--main" : "",
                draggingId === img.id ? "is-dragging" : "",
              ]
                .filter(Boolean)
                .join(" ")}
              data-media-id={img.id}
              data-testid={`property-media-card-${img.id}`}
            >
              <div className="property-media-card__preview">
                <img
                  src={img.image || FALLBACK_IMAGE}
                  alt={`${title} ${idx + 1}`}
                />
                <KindBadge kind={kind} />
                {isMain ? <MainBadge /> : null}
              </div>
              <div className="property-media-card__actions">
                {canReorder ? (
                  <button
                    type="button"
                    className="property-media-card__handle"
                    aria-label="Перетягнути для зміни порядку. Стрілки також змінюють порядок."
                    data-testid={`property-media-handle-${img.id}`}
                    disabled={busy}
                    onPointerDown={(event) => onHandlePointerDown(event, img.id)}
                    onPointerUp={onHandlePointerUp}
                    onPointerCancel={onHandlePointerUp}
                    onLostPointerCapture={(event) => {
                      if (event.buttons) return;
                      endDrag();
                    }}
                    onKeyDown={(event) => onHandleKeyDown(event, img.id)}
                  >
                    <i className="fa-solid fa-grip-vertical" aria-hidden="true" />
                  </button>
                ) : null}
                {allowMakeMain && !isMain ? (
                  <button
                    type="button"
                    className="action-btn property-media-card__make-main"
                    disabled={busy}
                    onClick={() => onMakeMain?.(img.id)}
                    aria-label={`Зробити головним фото ${idx + 1}`}
                    data-testid={`property-media-make-main-${img.id}`}
                  >
                    Зробити головною
                  </button>
                ) : null}
                <button
                  type="button"
                  className="action-btn property-media-card__edit"
                  disabled={busy}
                  onClick={(event) => {
                    restoreFocusRef.current = event.currentTarget;
                    onEditItem?.({
                      imageId: img.id,
                      kind,
                      sourceUrl: img.image,
                    });
                  }}
                  aria-label={`Редагувати ${noun} ${idx + 1}`}
                  data-testid={`property-media-edit-${img.id}`}
                >
                  Редагувати
                </button>
                <button
                  type="button"
                  className="property-media-card__delete"
                  aria-label={`Видалити ${noun} ${idx + 1}`}
                  disabled={busy}
                  onClick={() =>
                    setDeleteTarget({
                      type: "server",
                      id: img.id,
                      label: `${title} ${idx + 1}`,
                    })
                  }
                >
                  <i className="fa-solid fa-xmark" aria-hidden="true" />
                </button>
              </div>
            </li>
          );
        })}

        {(localItems || []).map((item, idx) => {
          const isPendingMain =
            showPendingMain && pendingMainLocalId === item.localId;
          const canEdit =
            item.phase === QUEUE_PHASE.ready && item.uploadFile && !busy;
          const showPrepareRetry =
            item.phase === QUEUE_PHASE.prepare_failed && canRetryPrepare(item);
          const showUploadRetry =
            item.phase === QUEUE_PHASE.upload_failed && canRetryUpload(item);
          const localIndex = orderedServer.length + idx + 1;
          return (
            <li
              key={`local-${item.localId}`}
              className={[
                "property-media-card",
                isPendingMain ? "property-media-card--main property-media-card--pending-main" : "",
              ]
                .filter(Boolean)
                .join(" ")}
              data-testid={`property-media-local-${item.localId}`}
            >
              <div className="property-media-card__preview">
                {item.previewUrl ? (
                  <img src={item.previewUrl} alt={`${title} ${localIndex}`} />
                ) : (
                  <div
                    className="property-media-card__pending"
                    role="status"
                    aria-label={`${item.file?.name || "HEIC"} підготовка`}
                  >
                    <span className="property-media-card__pending-label">HEIC</span>
                    <span className="property-media-card__pending-name">
                      {item.file?.name || "файл"}
                    </span>
                    <span className="property-media-card__pending-hint">
                      {queuePhaseLabel(item.phase)}
                    </span>
                  </div>
                )}
                <KindBadge kind={kind} />
                {isPendingMain ? <MainBadge /> : null}
              </div>
              <LocalStatus item={item} />
              <div className="property-media-card__actions">
                {showPendingMain && !isPendingMain ? (
                  <button
                    type="button"
                    className="action-btn property-media-card__make-main"
                    disabled={busy || item.phase === QUEUE_PHASE.uploading}
                    onClick={() => onMakePendingMain?.(item.localId)}
                    aria-label={`Зробити головним очікуване фото ${idx + 1}`}
                  >
                    Зробити головною
                  </button>
                ) : null}
                {canEdit ? (
                  <button
                    type="button"
                    className="action-btn property-media-card__edit"
                    disabled={busy}
                    onClick={(event) => {
                      restoreFocusRef.current = event.currentTarget;
                      onEditItem?.({
                        localId: item.localId,
                        kind,
                        sourceFile: item.uploadFile,
                      });
                    }}
                    aria-label={`Редагувати ${noun} ${localIndex}`}
                    data-testid={`property-media-edit-local-${item.localId}`}
                  >
                    Редагувати
                  </button>
                ) : null}
                {showPrepareRetry ? (
                  <button
                    type="button"
                    className="property-media-card__retry-icon"
                    disabled={busy}
                    onClick={() => onRetryPrepare?.(item.localId, kind)}
                    aria-label={`Повторити підготовку ${idx + 1}`}
                  >
                    <i className="fa-solid fa-rotate-right" aria-hidden="true" />
                  </button>
                ) : null}
                {showUploadRetry ? (
                  <button
                    type="button"
                    className="btn btn-outline-primary btn-sm property-media-card__retry"
                    disabled={busy}
                    onClick={() => onRetryUpload?.(item.localId, kind)}
                    aria-label={`Повторити завантаження ${idx + 1}`}
                    data-testid={`property-media-retry-${item.localId}`}
                  >
                    Повторити
                  </button>
                ) : null}
                <button
                  type="button"
                  className="property-media-card__delete"
                  aria-label={`Видалити ${noun} ${idx + 1}`}
                  disabled={busy || item.phase === QUEUE_PHASE.uploading}
                  onClick={() =>
                    setDeleteTarget({
                      type: "local",
                      id: item.localId,
                      label: `${title} ${idx + 1}`,
                    })
                  }
                >
                  <i className="fa-solid fa-xmark" aria-hidden="true" />
                </button>
              </div>
            </li>
          );
        })}
      </ul>

      <SharedImageEditorModal
        show={editorOpen}
        sourceFile={
          editorSource?.uploadFile
          || (editingItem?.imageId != null ? serverSourceFile : null)
        }
        kind={kind}
        title={kind === "plan" ? "Редагувати план" : "Редагувати фото"}
        onApply={(file) => {
          if (editorSource) {
            onApplyEdit?.(editorSource.localId, file, kind);
            return;
          }
          if (editingItem?.imageId != null) {
            onReplaceServer?.(editingItem.imageId, file, kind);
          }
        }}
        onCancel={onCancelEdit}
        disabled={busy}
        restoreFocusRef={restoreFocusRef}
        loading={
          Boolean(editorOpen && editingItem?.imageId != null && !serverSourceFile && !serverSourceError)
        }
        loadError={
          editorOpen && editingItem?.imageId != null ? serverSourceError : null
        }
        className="project-modal image-editor-modal"
        dialogClassName="image-editor-modal__dialog"
        contentClassName="image-editor-modal__content"
        bodyClassName="image-editor-modal__body"
        testId={
          editingItem?.imageId != null
            ? `property-media-server-editor-${kind}`
            : `property-media-local-editor-${kind}`
        }
        titleId={`property-media-editor-title-${kind}`}
      />

      <Dropzone
        inputId={inputId}
        title={dropTitle}
        disabled={busy}
        onFiles={onAdd}
      />

      <PropertyMediaDeleteDialog
        open={Boolean(deleteTarget)}
        title={confirmTitle}
        message={
          deleteTarget
            ? `Видалити ${deleteTarget.label}? Цю дію не можна скасувати.`
            : ""
        }
        busy={busy}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={() => {
          const target = deleteTarget;
          setDeleteTarget(null);
          if (!target) return;
          if (target.type === "server") onDeleteServer?.(target.id);
          else onDeleteLocal?.(target.id);
        }}
      />
    </div>
  );
}

export default PropertyMediaBoard;
